import FunnelHeader from '@/components/v1/FunnelHeader'
import FunnelLayout from '@/components/v1/FunnelLayout'
import { FUNNEL_PROGRESS_SECTIONS } from '@/pages/v1/onboarding/funnelSteps'
import { prefetchStripePrices } from '@app-studio-web/ui/helpers/supabase'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

// Matches the dwell time the shared preparing page waits before redirecting.
const PREPARING_DURATION_MS = 3000

interface Props {
  onPrepare?: () => Promise<{ success: boolean }>
  redirectTo?: string
}

/**
 * li-funnel-redesign v1 — the editorial preparing step. Same prepare/redirect
 * contract as the shared page, rendered as the funnel's closing loader.
 */
export default function PreparingPage({
  onPrepare,
  redirectTo = '/checkout/plans',
}: Props = {}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    // Kicked off a frame late so the bar animates from 0 instead of mounting full.
    const start = requestAnimationFrame(() => setProgress(100))

    return () => cancelAnimationFrame(start)
  }, [])

  // The dwell runs *alongside* the prepare call rather than after it, so the
  // wait is `max(prepare, 3s)` and not `prepare + 3s`. The shared page starts
  // its timer only once the call resolves, which stacks a cold edge function
  // invocation on top of the full 3s — dead time right before the paywall.
  //
  // It also navigates whether or not the save succeeded. Writing the answers is
  // best-effort analytics; the shared page only advances on `success`, so an
  // edge function failure strands the buyer on this screen with no way forward.
  useEffect(() => {
    let cancelled = false

    // The plans loader needs these the moment we redirect, so fetch them now
    // instead of after, while the dwell is otherwise doing nothing.
    prefetchStripePrices()

    const dwell = new Promise((resolve) =>
      setTimeout(resolve, PREPARING_DURATION_MS),
    )
    const prepare = onPrepare
      ? onPrepare().catch(() => undefined)
      : Promise.resolve()

    Promise.all([dwell, prepare]).then(() => {
      if (!cancelled) navigate(redirectTo)
    })

    return () => {
      cancelled = true
    }
  }, [navigate, onPrepare, redirectTo])

  return (
    <FunnelLayout
      header={
        <FunnelHeader
          section="yourAccount"
          sectionProgress={1}
          sections={FUNNEL_PROGRESS_SECTIONS}
        />
      }
    >
      <div className="flex grow flex-col items-center justify-center gap-8 py-10">
        <span
          aria-hidden
          className="bg-hw-primary-soft flex h-20 w-20 items-center justify-center rounded-full text-4xl"
        >
          <span className="animate-float">✨</span>
        </span>

        <div className="animate-fade-up space-y-2">
          <h1 className="hw-heading">
            {t('pages.signUp.v1.preparing.heading')}
          </h1>

          <p className="hw-subheading">
            {t('pages.signUp.v1.preparing.subtitle')}
          </p>
        </div>

        <div className="bg-hw-line h-2 w-full overflow-hidden rounded-full">
          <div
            className="bg-hw-primary h-full rounded-full ease-linear"
            style={{
              transitionDuration: `${PREPARING_DURATION_MS}ms`,
              transitionProperty: 'width',
              width: `${progress}%`,
            }}
          />
        </div>
      </div>
    </FunnelLayout>
  )
}
